import React, { useEffect, useRef, useState, useCallback } from "react";
import PropTypes from "prop-types";

function ResponsiveCarousel({
  items,
  autoplay = false,
  interval = 4000,
  loop = true,
  showArrows = true,
  showIndicators = true,
  breakpoints = { 0: 1, 600: 1, 900: 2, 1200: 3 },
  stagePadding = 0, // px padding on each side (not strictly identical to Owl but supported)
  margin = 8, // px gap between slides
  ariaLabel = "Image carousel",
}) {
  const containerRef = useRef(null);
  const trackRef = useRef(null);
  const autoplayRef = useRef(null);
  const transitionRef = useRef(false);
  const pointerRef = useRef({ active: false, startX: 0, currentOffset: 0 });
  const [slidesPerView, setSlidesPerView] = useState(1);
  const [currentIndex, setCurrentIndex] = useState(0); // index inside displayItems
  const [isPaused, setIsPaused] = useState(false);

  // compute slidesPerView from breakpoints (based on container width)
  const updateSlidesPerView = useCallback(() => {
    const width = containerRef.current?.clientWidth || window.innerWidth;
    const sorted = Object.keys(breakpoints).map(Number).sort((a, b) => a - b);
    let matched = breakpoints[sorted[0]];
    for (const bp of sorted) {
      if (width >= bp) matched = breakpoints[bp];
    }
    //setSlidesPerView(Math.max(1, Math.floor(matched)));
  }, [breakpoints]);

  useEffect(() => {
    updateSlidesPerView();
    const ro = new ResizeObserver(() => updateSlidesPerView());
    if (containerRef.current) ro.observe(containerRef.current);
    window.addEventListener("orientationchange", updateSlidesPerView);
    return () => {
      ro.disconnect();
      window.removeEventListener("orientationchange", updateSlidesPerView);
    };
  }, [updateSlidesPerView]);

  // When slidesPerView or items change, rebuild clones and reset index to first real
  const getCloneCount = (spv) => (loop && items.length > spv ? spv : 0);

  const [displayItems, setDisplayItems] = useState(items);
  useEffect(() => {
      const cloneCount = getCloneCount(slidesPerView);
      console.log('Updating display items, cloneCount: ' + cloneCount);
    if (cloneCount > 0) {
      const head = items.slice(0, cloneCount);
      const tail = items.slice(-cloneCount);
      setDisplayItems([...tail, ...items, ...head]);
      setCurrentIndex(cloneCount);
    } else {
      setDisplayItems(items);
      setCurrentIndex(0);
    }
    // expose slides-per-view for CSS sizing and margin variable
    if (containerRef.current) {
      containerRef.current.style.setProperty("--slides-per-view", slidesPerView);
      containerRef.current.style.setProperty("--carousel-gap", `${margin}px`);
      containerRef.current.style.setProperty("--stage-padding", `${stagePadding}px`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, slidesPerView, loop, margin, stagePadding]);

  // helper values
  const cloneCount = getCloneCount(slidesPerView);
  const realStart = cloneCount;
  const realEnd = cloneCount + Math.max(0, items.length - slidesPerView);

  // navigation helpers
    const goToIndex = (target) => {
    if (displayItems.length < slidesPerView) {
      setCurrentIndex(0);
      return;
    }
    setCurrentIndex(target);
  };

  const next = () => {
     if (currentIndex <= items.length)
         goToIndex(currentIndex + 1);
     else
         goToIndex(0);
  };
  const prev = () => {
      if (currentIndex >= 1)
          goToIndex(currentIndex - 1);
      else
          goToIndex(items.length);
  };

  // smooth reset after reaching clones to create infinite effect
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;

    const onTransitionEnd = () => {
      transitionRef.current = false;
      // if we moved past real area, jump to equivalent real index without transition
      if (cloneCount === 0) return;
      if (currentIndex <= realStart - 1) {
        // moved to left clones -- jump forward by items.length
        const jumpTo = currentIndex + items.length;
        el.style.transition = "none";
        el.style.transform = `translateX(-${(jumpTo * 100) / slidesPerView}%)`;
        // force reflow then restore transition
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        //el.offsetHeight;
        setCurrentIndex(jumpTo);
        el.style.transition = "";
      } else if (currentIndex > realEnd) {
        // moved to right clones -- jump back by items.length
        const jumpTo = currentIndex - items.length;
        el.style.transition = "none";
        el.style.transform = `translateX(-${(jumpTo * 100) / slidesPerView}%)`;
        // force reflow
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
          //el.offsetHeight;
          console.log('Jumping back to index: ' + jumpTo);
        setCurrentIndex(jumpTo);
        el.style.transition = "";
      }
    };

    el.addEventListener("transitionend", onTransitionEnd);
    return () => el.removeEventListener("transitionend", onTransitionEnd);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, cloneCount, realStart, realEnd, items.length, slidesPerView]);

  // update transform whenever currentIndex changes (use transition unless we are programmatically resetting)
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    transitionRef.current = true;
    el.style.transform = `translateX(-${(currentIndex * 100) / slidesPerView}%)`;
  }, [currentIndex, slidesPerView]);

  // autoplay
  useEffect(() => {
    if (!autoplay || isPaused || displayItems.length <= slidesPerView) return;
    autoplayRef.current = setInterval(() => {
      next();
    }, interval);
    return () => clearInterval(autoplayRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoplay, interval, isPaused, currentIndex, slidesPerView, displayItems.length]);

  // pause on hover/focus
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onEnter = () => setIsPaused(true);
    const onLeave = () => setIsPaused(false);
    el.addEventListener("mouseenter", onEnter);
    el.addEventListener("mouseleave", onLeave);
    el.addEventListener("focusin", onEnter);
    el.addEventListener("focusout", onLeave);
    return () => {
      el.removeEventListener("mouseenter", onEnter);
      el.removeEventListener("mouseleave", onLeave);
      el.removeEventListener("focusin", onEnter);
      el.removeEventListener("focusout", onLeave);
    };
  }, []);

  // keyboard navigation
  useEffect(() => {
    const onKey = (e) => {
      if (document.activeElement && containerRef.current?.contains(document.activeElement)) {
        if (e.key === "ArrowRight") next();
        if (e.key === "ArrowLeft") prev();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, slidesPerView, displayItems.length]);

  // pointer (touch/drag)
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;

    const onPointerDown = (e) => {
      pointerRef.current.active = true;
      const clientX = e.clientX ?? (e.touches && e.touches[0]?.clientX);
      pointerRef.current.startX = clientX;
      pointerRef.current.currentOffset = 0;
      el.style.transition = "none";
    };
    const onPointerMove = (e) => {
      if (!pointerRef.current.active) return;
      const clientX = e.clientX ?? (e.touches && e.touches[0]?.clientX);
      pointerRef.current.currentOffset = clientX - pointerRef.current.startX;
      const width = containerRef.current.clientWidth;
      const percentage = (pointerRef.current.currentOffset / width) * 100;
      el.style.transform = `translateX(calc(-${(currentIndex * 100) / slidesPerView}% + ${percentage}%))`;
    };
    const onPointerUp = () => {
      if (!pointerRef.current.active) return;
      el.style.transition = "";
      const width = containerRef.current.clientWidth;
      const moved = pointerRef.current.currentOffset;
      pointerRef.current.active = false;
      if (Math.abs(moved) > Math.min(30, width * 0.15)) {
        if (moved < 0) next();
        else prev();
      } else {
        // reset position
        el.style.transform = `translateX(-${(currentIndex * 100) / slidesPerView}%)`;
      }
      pointerRef.current.currentOffset = 0;
    };

    el.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
    // touch fallback
    el.addEventListener("touchstart", onPointerDown, { passive: true });
    window.addEventListener("touchmove", onPointerMove, { passive: true });
    window.addEventListener("touchend", onPointerUp);

    return () => {
      el.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
      el.removeEventListener("touchstart", onPointerDown);
      window.removeEventListener("touchmove", onPointerMove);
      window.removeEventListener("touchend", onPointerUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, slidesPerView, displayItems.length]);

  // indicator count is number of possible start positions of real items
  const indicatorCount = Math.max(1, Math.max(0, items.length)); // - slidesPerView + 1));

  // If not enough items to scroll, disable looping and controls
  const isStatic = items.length <= slidesPerView;

  return (
    <div
      className="rc-carousel"
      ref={containerRef}
      role="region"
      aria-label={ariaLabel}
      tabIndex={0}
      style={{ paddingLeft: stagePadding, paddingRight: stagePadding }}
    >
      {showArrows && !isStatic && (
        <button
          className="rc-arrow rc-arrow--prev"
          onClick={prev}
          aria-label="Previous slide">
          <span className="icon-left-open"></span>
        </button>
      )}

      <div className="rc-viewport">
        <div
          className="rc-track"
          ref={trackRef}
          style={{
            width: `${(displayItems.length * 100) / slidesPerView}%`,
          }}
        >
          {displayItems.map((it, idx) => (
            <div
              className="rc-slide"
              key={idx}
              aria-hidden={idx < currentIndex || idx >= currentIndex + slidesPerView ? "true" : "false"}
            >
                  <a href={it.Url || it.url || "#"} target="_blank" rel="noopener noreferrer" style={{ width: containerRef.current?.clientWidth || window.innerWidth }}>
                <img src={it.ImageUrl || it.src || it.url} alt={it.Title || it.alt || `Slide ${idx + 1}`} loading="lazy" />
              </a>
              {it.Title && <div className="rc-caption">{it.Title}</div>}
            </div>
          ))}
        </div>
      </div>

      {showArrows && !isStatic && (
        <button
          className="rc-arrow rc-arrow--next"
          onClick={next}
          aria-label="Next slide"
        >
          <span className="icon-right-open"></span>
        </button>
      )}

      {showIndicators && (
        <div className="rc-indicators" role="tablist" aria-label="Slide indicators">
          {Array.from({ length: indicatorCount }).map((_, i) => {
            // map visual indicator i to displayItems index (realStart + i)
            const mappedIndex = realStart + i;
            const isActive = currentIndex === mappedIndex || (isStatic && i === 0);
            return (
              <button
                key={i}
                className={`rc-indicator ${isActive ? "is-active" : ""}`}
                onClick={() => goToIndex(mappedIndex)}
                aria-label={`Go to slide ${i + 1}`}
                aria-current={isActive ? "true" : "false"}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

ResponsiveCarousel.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  autoplay: PropTypes.bool,
  interval: PropTypes.number,
  loop: PropTypes.bool,
  showArrows: PropTypes.bool,
  showIndicators: PropTypes.bool,
  breakpoints: PropTypes.object,
  stagePadding: PropTypes.number,
  margin: PropTypes.number,
  ariaLabel: PropTypes.string,
};

export default ResponsiveCarousel;